import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Snackbar, Alert, CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { getSearchParams, getParams } from "../../../utlis/helpers";
import { PERSON_DASHBOARD } from "../../../routes/routerPath";
import {
  getSensorMapping,
  assignSensorToSenior,
  unassignSensorFromSenior,
  getActivityMonitorData,
} from "../../../services/activityMonitoringApi";
import { getUserList } from "../../../services/websiteApi";
import { userInfoFetch, formatName } from "../../../utlis/commonFunction";
import { getCurrentLocale } from "../../../utlis/locale";
import { useSection } from "../../../i18n/useSection";
import { getSensorType } from "./sensorTypes";
import SensorTypeSetup from "./SensorTypeSetup";
import {
  localizeActivityText,
  localizeActivityTree,
} from "./activityMonitoringI18n";
import "./activitymonitoring.scss";

/**
 * Fall & Activity Monitoring screen.
 *
 * Sensor assignment gating (getSensorMapping / assignSensorToSenior) is
 * preserved from the previous implementation — same APIs. Once a sensor is
 * mapped, the screen renders the "Fall & Activity Monitoring" UI resolved from
 * the sensor type in the normalized backend payload (getActivityMonitorData).
 * A wrong/unrecognized sensor is surfaced as an error (with Retry) rather than
 * falling back to bundled sample data — the mapped sensor's real data is the
 * only source of truth.
 */
const ActivityMonitoring = () => {
  const navigate = useNavigate();
  const t = useSection("activityMonitoring");
  const locale = getCurrentLocale();
  const [isLoading, setIsLoading] = useState(true);
  const [monitorData, setMonitorData] = useState(null);
  const [dataError, setDataError] = useState(null);

  // Sensor setup / mapping state (assign API unchanged)
  const [assignedSensorId, setAssignedSensorId] = useState(null);
  const [assignedSensorType, setAssignedSensorType] = useState(null);
  // Inline Sensor ID editor shown on the error screen (edit + retry a wrong sensor).
  const [editingSensor, setEditingSensor] = useState(false);
  const [sensorInput, setSensorInput] = useState("");
  const [savingSensor, setSavingSensor] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [mappingRetryKey, setMappingRetryKey] = useState(0);

  const [userInfo, setUserInfo] = useState(() => userInfoFetch());

  const { seniorId: seniorIdFromParams } = getParams();
  // Resolve senior UUID — consistent with PersonDashboard and other v5 consumers.
  const seniorUuid = seniorIdFromParams || userInfo?.id;

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  // Bootstrap user info from API when sessionStorage is empty (direct navigation)
  useEffect(() => {
    if (!seniorIdFromParams || userInfo?.id) return;
    let mounted = true;
    getUserList(1, 999)
      .then((res) => {
        const users = res?.data?.data || [];
        sessionStorage.setItem("userList", JSON.stringify(users));
        const idx = users.findIndex((u) => u.id === seniorIdFromParams);
        if (idx !== -1 && mounted) {
          sessionStorage.setItem("userIndex", idx);
          setUserInfo(users[idx]);
        }
      })
      .catch((err) => console.error("Error bootstrapping user info:", err));
    return () => {
      mounted = false;
    };
  }, [seniorIdFromParams, userInfo?.id]);

  // Check for an assigned sensor (gate before showing the monitoring UI)
  useEffect(() => {
    let mounted = true;
    const checkSensorAssociation = async () => {
      if (!seniorUuid) {
        if (mounted) {
          setDataError(t.senior_missing);
          setIsLoading(false);
        }
        return;
      }
      setIsLoading(true);
      try {
        const response = await getSensorMapping(seniorUuid);
        if (!mounted) return;
        const isSuccess = response.success || response.status === 1;
        if (isSuccess && response.data?.sensorId) {
          setDataError(null);
          setAssignedSensorId(response.data.sensorId);
          setAssignedSensorType(
            response.data.sensor_type || response.data.sensorType || null,
          );
          // Keep loading until the monitoring payload resolves.
        } else if (isSuccess) {
          setDataError(null);
          setAssignedSensorId(null);
          setAssignedSensorType(null);
          setIsLoading(false);
        } else {
          setAssignedSensorId(null);
          setAssignedSensorType(null);
          setDataError(response.message || t.mapping_failed);
          setIsLoading(false);
        }
      } catch (err) {
        console.error("Error checking sensor:", err);
        if (mounted) {
          setAssignedSensorId(null);
          setAssignedSensorType(null);
          setDataError(err.message || t.mapping_failed);
          setIsLoading(false);
        }
      }
    };
    checkSensorAssociation();
    return () => {
      mounted = false;
    };
  }, [seniorUuid, mappingRetryKey, t.mapping_failed, t.senior_missing]);

  // Overlay the real resident identity onto the payload so the header shows the
  // selected senior even when the backend/sample resident differs.
  const withResident = useCallback(
    (data) => {
      if (!data) return data;
      const name = formatName(userInfo, getCurrentLocale());
      return {
        ...data,
        resident: {
          ...data.resident,
          ...(name ? { name } : {}),
          ...(userInfo?.gender ? { gender: userInfo.gender } : {}),
          ...(userInfo?.age ? { age: userInfo.age } : {}),
          ...(userInfo?.room ? { location: userInfo.room } : {}),
        },
      };
    },
    [userInfo],
  );

  // Fetch the normalized Fall & Activity Monitoring payload. Returns true only
  // when a valid payload for a recognized sensor was loaded. On a wrong/invalid
  // sensor we do NOT fall back to bundled sample data — a non-silent load shows
  // the error/Retry state; a silent poll keeps the last good data on screen.
  const loadMonitorData = useCallback(
    async (silent = false) => {
      if (!seniorUuid) return false;
      if (!silent) setIsLoading(true);
      try {
        const res = await getActivityMonitorData(seniorUuid);
        const data = res?.data || res;
        const isValid = Boolean(
          data && data.resident && getSensorType(data.sensor?.type),
        );
        if (isValid) {
          setMonitorData(withResident(data));
          setDataError(null);
          return true;
        }
        if (!silent) {
          setMonitorData(null);
          setDataError(data && data.resident ? t.unrecognized : t.load_failed);
        }
        return false;
      } catch (err) {
        console.error("Activity monitor load failed.", err);
        if (!silent) {
          setMonitorData(null);
          setDataError(
            err.response?.data?.message || err.message || t.load_failed,
          );
        }
        return false;
      } finally {
        if (!silent) setIsLoading(false);
      }
    },
    [seniorUuid, t.load_failed, t.unrecognized, withResident],
  );

  useEffect(() => {
    if (assignedSensorId) loadMonitorData();
  }, [assignedSensorId, loadMonitorData]);

  // Poll for live status updates every 60s (silent — no spinner).
  useEffect(() => {
    if (!assignedSensorId) return;
    const id = setInterval(() => loadMonitorData(true), 60000);
    return () => clearInterval(id);
  }, [assignedSensorId, loadMonitorData]);

  const handleBack = () => {
    navigate(PERSON_DASHBOARD + getSearchParams());
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSensorMappingChange = async (action, sensorId, sensorType) => {
    if (!seniorUuid) {
      showSnackbar(t.missing_senior_update, "error");
      return false;
    }

    if ((action === "attach" || action === "update") && !sensorId?.trim()) {
      showSnackbar(t.enter_sensor, "error");
      return false;
    }

    try {
      if (action === "detach") {
        const response = await unassignSensorFromSenior(seniorUuid);
        const isSuccess = response.success || response.status === 1;
        if (!isSuccess) {
          showSnackbar(response.message || t.remove_failed, "error");
          return false;
        }
        setAssignedSensorId(null);
        setAssignedSensorType(null);
        setMonitorData(null);
        showSnackbar(t.mapping_removed);
        return true;
      }

      const resolvedSensorType =
        sensorType ||
        assignedSensorType ||
        monitorData?.sensor?.type ||
        "pontosense";
      const response = await assignSensorToSenior(seniorUuid, sensorId.trim());
      const isSuccess = response.success || response.status === 1;
      if (!isSuccess) {
        showSnackbar(response.message || t.update_failed, "error");
        return false;
      }

      // The mapping is saved, but the entered sensor may be wrong/unrecognized —
      // only report success once its real data actually loads. Otherwise leave
      // the error/Retry state visible instead of a misleading success.
      const reportedSensorType =
        response.data?.sensor_type ||
        response.data?.sensorType ||
        resolvedSensorType;
      setAssignedSensorId(sensorId.trim());
      setAssignedSensorType(reportedSensorType);
      const loadedValidData = await loadMonitorData();
      if (!loadedValidData) {
        showSnackbar(t.saved_no_data, "error");
        return false;
      }
      showSnackbar(action === "attach" ? t.linked : t.updated);
      return true;
    } catch (err) {
      console.error("Sensor mapping update failed:", err);
      showSnackbar(t.update_error, "error");
      return false;
    }
  };

  // Save a corrected Sensor ID from the error screen. Reuses the same mapping
  // flow ('update'); on success the valid payload loads and the error clears.
  const handleSaveEditedSensor = async () => {
    if (!sensorInput.trim()) {
      showSnackbar(t.enter_sensor, "error");
      return;
    }
    setSavingSensor(true);
    const ok = await handleSensorMappingChange(
      "update",
      sensorInput,
      assignedSensorType,
    );
    setSavingSensor(false);
    // On success monitorData loads and this screen unmounts; on failure keep the
    // editor open so the user can correct the value.
    if (ok) setEditingSensor(false);
  };

  const residentOnlyData = withResident({
    resident: { id: seniorUuid },
    sensor: null,
  });
  const viewData = monitorData || residentOnlyData;
  const sensorType = getSensorType(
    viewData?.sensor?.type || assignedSensorType,
  );
  const localizedViewData = useMemo(
    () => localizeActivityTree(viewData, locale),
    [locale, viewData],
  );
  const localizedLogic = useMemo(
    () => localizeActivityTree(sensorType?.logic, locale),
    [locale, sensorType?.logic],
  );

  // Loading state
  if (isLoading) {
    return (
      <div className="activity-monitoring-wrapper">
        <div
          className="loading-container"
          style={{ margin: "auto", marginTop: "2rem" }}
        >
          <CircularProgress size={32} />
        </div>
      </div>
    );
  }

  // Error state (only when we have no data to show)
  if (dataError && !monitorData) {
    return (
      <div className="activity-monitoring-wrapper">
        <header className="activity-error-header">
          <button type="button" onClick={handleBack} aria-label={t.back}>
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M19 12H5m7-7-7 7 7 7" />
            </svg>
          </button>
          <h1>{t.title}</h1>
        </header>
        <div className="error-container">
          <div className="error-text">
            {localizeActivityText(dataError, locale)}
          </div>
          {assignedSensorId && editingSensor ? (
            <div className="error-edit-sensor">
              <input
                className="error-sensor-input"
                type="text"
                value={sensorInput}
                onChange={(e) => setSensorInput(e.target.value)}
                placeholder={t.sensor_placeholder}
                disabled={savingSensor}
                autoFocus
              />
              <div className="error-edit-actions">
                <button
                  className="btn btn-primary"
                  onClick={handleSaveEditedSensor}
                  disabled={savingSensor}
                >
                  {savingSensor ? t.saving : t.save_retry}
                </button>
                <button
                  className="btn btn-outline"
                  onClick={() => setEditingSensor(false)}
                  disabled={savingSensor}
                >
                  {t.cancel}
                </button>
              </div>
            </div>
          ) : assignedSensorId ? (
            <>
              <button
                className="btn btn-primary"
                onClick={() => loadMonitorData()}
              >
                {t.retry}
              </button>
              <button
                className="btn btn-outline"
                onClick={() => {
                  setSensorInput(assignedSensorId || "");
                  setEditingSensor(true);
                }}
              >
                {t.edit_sensor}
              </button>
            </>
          ) : (
            <button
              className="btn btn-primary"
              onClick={() => setMappingRetryKey((key) => key + 1)}
            >
              {t.retry}
            </button>
          )}
        </div>
      </div>
    );
  }

  // Fall & Activity Monitoring UI. When no sensor is mapped, accept one Sensor
  // ID and let the backend identify its type. Once mapped, resolve the renderer
  // from the backend-reported sensor.type;
  // an unrecognized type is treated as a wrong sensor (handled as an error in
  // loadMonitorData), so a valid monitorData here always resolves a type.
  if (!assignedSensorId) {
    return (
      <div className="activity-monitoring-screen">
        <SensorTypeSetup
          onBack={handleBack}
          saving={savingSensor}
          onLink={async (sensorId) => {
            setSavingSensor(true);
            const ok = await handleSensorMappingChange("attach", sensorId);
            setSavingSensor(false);
            return ok;
          }}
        />

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            closeText={t.close}
            severity={snackbarSeverity}
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </div>
    );
  }

  const MonitorView = sensorType?.MonitorView;
  return (
    <div className="activity-monitoring-screen">
      {viewData && MonitorView && (
        <MonitorView
          data={localizedViewData}
          logic={localizedLogic}
          onBack={handleBack}
          onSensorMappingChange={(action, sensorId) =>
            handleSensorMappingChange(action, sensorId, sensorType.id)
          }
        />
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          closeText={t.close}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {localizeActivityText(snackbarMessage, locale)}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ActivityMonitoring;
